import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useGetList, useGetOne, useNotify } from "react-admin";
import { AlertType, Client, DocumentRead, DocumentTransformation, ResourceName } from "@/types";
import { FieldMappingDialog } from "./field-mapping/FieldMappingDialog";
import { useTransformationRecords } from "../hooks/useTransformationRecords";
import { isSpreadsheetDocument, isPdfDocument } from "../utils/documentFormat";
import { useState, useMemo, useEffect } from "react";
import { ButtonStyleType } from "@/types/shared/uiComponents";

interface ReprocessDialogProps {
  open: boolean;
  onClose: () => void;
  record: DocumentRead;
  onRetry: (id: string, clientId?: string) => void;
}

/**
 * Self-contained reprocess dialog that handles:
 * - Transformation checking (fetches on mount)
 * - Client selection when client_id_ref is missing
 * - Field mapping adjustment via FieldMappingDialog
 * - Reprocess-without-changes action
 */
export const ReprocessDialog = ({ open, onClose, record, onRetry }: ReprocessDialogProps) => {
  const notify = useNotify();
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [openFieldMapping, setOpenFieldMapping] = useState(false);
  // Server-side search (#5697) so every client is reachable regardless of tenant
  // size — typing drives a `q` query instead of loading a capped page.
  const [clientSearch, setClientSearch] = useState("");

  const clientId = record.client_id_ref ?? selectedClient?.id;
  const requiresClientSelection = !record.client_id_ref;

  // Fetch transformations when dialog is open
  // No expand=extracted_records (#6755). This dialog only ever read
  // transformation_template_id_ref off the result — the record set was fetched
  // and thrown away. Harmless-looking until you open this dialog on a
  // 497,711-record document, where serialising it OOM-kills mgmt-api (#6826).
  const { data: transformations, isLoading: isLoadingTransformations } = useGetList<DocumentTransformation>(
    `documents/${record.id}/document-transformations`,
    {},
    { enabled: open }
  );

  const latestTransformation = useMemo(
    () => (transformations && transformations.length > 0 ? transformations[0] : null),
    [transformations]
  );

  // One page, for the critical-field advisory's conditional entries (#6755).
  // This dialog used to get records free with the expand it no longer sends.
  const { records: advisoryRecords } = useTransformationRecords({
    documentId: record.id,
    transformationId: latestTransformation?.id,
    enabled: open && !!latestTransformation?.id,
    autoLoadUpTo: 0,
  });

  // Close dialog if no transformation exists
  useEffect(() => {
    if (open && !isLoadingTransformations && !latestTransformation) {
      notify("Cannot reprocess: No transformation history found for this document", { type: AlertType.WARNING });
      onClose();
    }
  }, [open, isLoadingTransformations, latestTransformation, notify, onClose]);

  // Fetch template if available
  const { data: transformationTemplate, isLoading: isLoadingTemplate } = useGetOne(
    "transformation-templates",
    { id: latestTransformation?.transformation_template_id_ref || "" },
    { enabled: !!latestTransformation?.transformation_template_id_ref && open }
  );

  // Fetch clients if needed
  const { data: clients = [], isLoading: isLoadingClients } = useGetList(
    ResourceName.CLIENTS,
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "client_legal_name", order: "ASC" },
      filter: clientSearch ? { q: clientSearch } : {},
    },
    { enabled: open && requiresClientSelection }
  );

  // Keep the picked client present in the options even when it's outside the freshly
  // loaded search page, so MUI doesn't warn "value not in options" on re-open.
  const clientOptions = useMemo(() => {
    if (selectedClient && !clients.some((c) => c.id === selectedClient.id)) {
      return [selectedClient, ...clients];
    }
    return clients;
  }, [clients, selectedClient]);

  const isLoadingData = isLoadingTransformations || isLoadingTemplate;

  const handleClose = () => {
    setSelectedClient(null);
    setClientSearch("");
    onClose();
  };

  const handleAdjustMapping = () => {
    if (!latestTransformation) return;
    onClose();
    setOpenFieldMapping(true);
  };

  const handleReprocessWithoutChanges = () => {
    if (requiresClientSelection && !selectedClient) return;
    onClose();
    onRetry(record.id, selectedClient?.id);
    setSelectedClient(null);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} onClick={(e) => e.stopPropagation()}>
        <DialogTitle variant="h3">Reprocess Document</DialogTitle>
        <DialogContent>
          {isLoadingData ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Would you like to adjust the field mapping before reprocessing this document?
              </DialogContentText>

              {requiresClientSelection && (
                <>
                  {!selectedClient ? (
                    <Alert severity={AlertType.WARNING} sx={{ mb: 2 }}>
                      This document does not have a client assigned. Please select a client before reprocessing.
                    </Alert>
                  ) : (
                    <Alert severity={AlertType.SUCCESS} sx={{ mb: 2 }}>
                      Client selected: {selectedClient.client_legal_name}
                    </Alert>
                  )}
                  <Autocomplete
                    size="small"
                    options={clientOptions}
                    filterOptions={(x) => x}
                    getOptionLabel={(option) => option.client_legal_name || ""}
                    isOptionEqualToValue={(option, val) => option.id === val.id}
                    value={selectedClient}
                    onChange={(_event, newValue) => setSelectedClient(newValue)}
                    onInputChange={(_event, value, reason) => {
                      if (reason === "input" || reason === "clear") setClientSearch(value);
                    }}
                    loading={isLoadingClients}
                    noOptionsText={clientSearch ? "No matches found" : "Type to search..."}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Client"
                        variant="outlined"
                        required
                        error={!selectedClient}
                        helperText={
                          !selectedClient
                            ? "Client selection is required"
                            : "Client will be assigned during reprocessing"
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoadingClients && <CircularProgress color="inherit" size={20} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <li key={key} {...otherProps}>
                          <Typography variant="body2">{option.client_legal_name}</Typography>
                        </li>
                      );
                    }}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant={ButtonStyleType.Text}>
            Cancel
          </Button>
          <Button
            onClick={handleReprocessWithoutChanges}
            variant={ButtonStyleType.Outlined}
            disabled={isLoadingData || (requiresClientSelection && !selectedClient)}
          >
            Reprocess Without Changes
          </Button>
          <Button
            onClick={handleAdjustMapping}
            variant="contained"
            disabled={isLoadingData || !latestTransformation || (requiresClientSelection && !selectedClient)}
          >
            Adjust Field Mapping
          </Button>
        </DialogActions>
      </Dialog>

      {latestTransformation && clientId && (
        <FieldMappingDialog
          open={openFieldMapping}
          onClose={() => setOpenFieldMapping(false)}
          transformation={latestTransformation}
          transformationTemplates={transformationTemplate ? [transformationTemplate] : []}
          clientId={clientId}
          documentId={record.id}
          isSpreadsheet={isSpreadsheetDocument(record)}
          isPdf={isPdfDocument(record)}
          // Extracted records let the blocking alert evaluate record-conditional
          // required fields (e.g. denial reason only when a denial amount is
          // present) instead of surfacing them unconditionally. Family-deductible
          // conditions need the review projection (not available here) and are
          // enforced downstream at Approve.
          advisoryContext={{ extractedRecords: advisoryRecords }}
        />
      )}
    </>
  );
};
